import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/apiClient'; // Assuming apiClient is configured for authenticated requests
import { API_ENDPOINTS } from '../config/api';
import { toast } from 'react-hot-toast';

const Settings = () => {
  const { user, updateUserContext } = useAuth(); // Assuming updateUserContext exists in AuthContext
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Pre-fill the username input with the current username when the component mounts or user changes
    if (user?.username) {
      setUsername(user.username);
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!user?.address) {
      setError('User not authenticated.');
      setLoading(false);
      return;
    }

    if (!username.trim()) {
      setError('Username cannot be empty.');
      setLoading(false);
      return;
    }
    
    // Basic username validation (e.g., length, characters - adjust as needed)
    if (username.length < 3 || username.length > 20) {
      setError('Username must be between 3 and 20 characters.');
      setLoading(false);
      return;
    }
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
       setError('Username can only contain letters, numbers, and underscores.');
       setLoading(false);
       return;
    }


    try {
      // Assuming an endpoint like PUT /api/users/me/username exists
      const response = await api.put(API_ENDPOINTS.users.updateUsername, { username });

      // Update user context locally
      if (updateUserContext) {
         updateUserContext({ username: response.username }); // Assuming response contains updated user or just username
      } else {
         // Fallback or alternative update method if updateUserContext is not available
         console.warn("updateUserContext not found in AuthContext. Local state might be stale.");
         // Potentially force a refresh or re-fetch user data
      }


      toast.success('Username updated successfully!');
    } catch (err) {
      console.error('Error updating username:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to update username.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <div className="p-4 text-white">Please connect your wallet.</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8 text-white">
      <h1 className="text-3xl font-bold mb-6">Settings</h1>
      <div className="bg-gray-800 p-6 rounded-lg max-w-md mx-auto">
        <h2 className="text-xl font-semibold mb-4">Update Username</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-1">
              Username
            </label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-dark border border-gray-700 text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Enter your desired username"
              required
              disabled={loading}
            />
             <p className="text-xs text-gray-500 mt-1">Letters, numbers, and underscores only (3-20 characters).</p>
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}

          <button
            type="submit"
            className={`w-full px-4 py-2 rounded-lg text-white font-semibold transition-colors ${
              loading
                ? 'bg-gray-600 cursor-not-allowed'
                : 'bg-primary hover:bg-primary-dark'
            }`}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Username'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Settings;
